<!-- vueui/src/components/NotificationBell.vue -->
<template>
  <div class="notification-bell">
    <!-- Notification Bell Icon with Badge -->
    <div class="notification-trigger" @click="toggleDropdown" ref="trigger">
      <CIcon icon="cil-bell" size="lg" class="text-light" />
      <CBadge 
        v-if="unreadCount > 0" 
        color="danger" 
        class="notification-badge"
        :class="{ 'pulse': unreadCount > 0 }"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </CBadge>
    </div>

    <!-- Notification Dropdown -->
    <div 
      v-if="showDropdown" 
      class="notification-dropdown"
      ref="dropdown"
    >
      <div class="notification-header">
        <h6 class="mb-0">Notifications (last 2h)</h6>
        <div class="notification-actions">
          <CButton 
            v-if="unreadCount > 0" 
            size="sm" 
            color="link" 
            @click="markAllAsRead"
            class="p-0 text-decoration-none"
          >
            Mark all read
          </CButton>
          <CButton 
            size="sm" 
            color="link" 
            @click="clearNotifications"
            class="p-0 text-decoration-none ms-2"
          >
            Clear all
          </CButton>
        </div>
      </div>

      <div class="notification-list">
        <div 
          v-if="notifications.length === 0" 
          class="text-center text-muted py-3"
        >
          No notifications
        </div>
        
        <div 
          v-for="notification in recentNotifications" 
          :key="notification.id"
          class="notification-item"
          :class="{ 'unread': notification.read !== 'true' }"
          @click="handleNotificationClick(notification)"
        >
          <div class="notification-icon">
            <CIcon 
              :icon="getNotificationIcon(notification.type)" 
              :class="getNotificationColor(notification.type)"
            />
          </div>
          <div class="notification-content">
            <div class="notification-title">{{ notification.title }}</div>
            <div class="notification-message">{{ notification.message }}</div>
            <div class="notification-time">{{ formatTime(notification.timestamp) }}</div>
          </div>
          <div class="notification-actions">
            <CButton 
              size="sm" 
              color="link" 
              @click.stop="removeNotification(notification.id)"
              class="p-0 text-decoration-none"
            >
              <CIcon icon="cil-x" size="sm" />
            </CButton>
          </div>
        </div>
      </div>

      <div class="notification-footer">
        <small class="text-muted">
          {{ isConnected ? 'Connected' : 'Disconnected' }}
        </small>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { CButton, CBadge } from '@coreui/vue'
import { useNotificationsStore } from '@/stores/notifications'
import { formatTstamp } from '@/assets/common'

const notificationsStore = useNotificationsStore()

// Reactive state
const showDropdown = ref(false)
const trigger = ref<HTMLElement>()
const dropdown = ref<HTMLElement>()

// Computed properties
const notifications = computed(() => notificationsStore.notifications)
const unreadCount = computed(() => notificationsStore.unreadCount)
const recentNotifications = computed(() => notificationsStore.recentNotifications)
const isConnected = computed(() => notificationsStore.isConnected)

// Methods
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
}

const markAllAsRead = () => {
  notificationsStore.markAllAsRead()
}

const clearNotifications = () => {
  notificationsStore.clearNotifications()
}

const removeNotification = (id: string) => {
  notificationsStore.removeNotification(id)
}

const handleNotificationClick = async (notification: any) => {
  if (!notification.read) {
    notificationsStore.markAsRead(notification.id)
  }
  
  // Handle PDF Generation Completed notifications
  //TODO: Use a constant for the PDF Generation Completed title, not a string literal; see ebNotificationProcessor.ts
  if (notification.title === 'PDF Generation Completed' && notification.data?.pdfS3Key) {
    try {
      console.log('Opening PDF for notification:', notification.id)
      
      // Import API_ENDPOINTS dynamically to avoid circular imports
      const { API_ENDPOINTS } = await import('@/assets/api')
      const { apiRequest } = await import('@/assets/api')
      
      // Generate pre-signed URL
      const response = await apiRequest(`${API_ENDPOINTS.BUILD.PDF_DOWNLOAD_URL}?pdfS3Key=${encodeURIComponent(notification.data.pdfS3Key)}`)
      
      if (response.ok) {
        const data = await response.json()
        
        // Open PDF in new tab
        window.open(data.downloadUrl, '_blank')
        console.log('PDF opened successfully:', notification.data.pdfS3Key)
      } else {
        console.error('Failed to generate PDF download URL:', response.statusText)
      }
    } catch (error) {
      console.error('Error opening PDF:', error)
    }
  } else if (notification.jobId) {
    // Could navigate to job details or book page for other notification types
    console.log('Notification clicked:', notification)
  }
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'pdf_completed': return 'cil-check-circle'
    case 'pdf_failed': return 'cil-x-circle'
    case 'pdf_started': return 'cil-play-circle'
    case 'webhook_received': return 'cil-git-branch'
    default: return 'cil-bell'
  }
}

const getNotificationColor = (type: string) => {
  switch (type) {
    case 'pdf_completed': return 'text-success'
    case 'pdf_failed': return 'text-danger'
    case 'pdf_started': return 'text-warning'
    case 'webhook_received': return 'text-info'
    default: return 'text-secondary'
  }
}

const formatTime = (timestamp: string) => {
  return formatTstamp(timestamp)
}

// Click outside to close dropdown
const handleClickOutside = (event: Event) => {
  if (showDropdown.value && 
      trigger.value && 
      dropdown.value && 
      !trigger.value.contains(event.target as Node) && 
      !dropdown.value.contains(event.target as Node)) {
    showDropdown.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  notificationsStore.startPolling()
  notificationsStore.requestNotificationPermission()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  notificationsStore.stopPolling()
})
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: inline-block;
}

.notification-trigger {
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.notification-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.notification-badge {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  font-size: 0.7rem;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-badge.pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: translate(50%, -50%) scale(1); }
  50% { transform: translate(50%, -50%) scale(1.1); }
  100% { transform: translate(50%, -50%) scale(1); }
}

.notification-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 350px;
  max-height: 500px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.notification-list {
  max-height: 350px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item:hover {
  background-color: #f8f9fa;
}

.notification-item.unread {
  background-color: #e3f2fd;
}

.notification-item.unread:hover {
  background-color: #bbdefb;
}

.notification-icon {
  flex-shrink: 0;
  margin-right: 12px;
  margin-top: 2px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
  color: #212529;
}

.notification-message {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 4px;
  line-height: 1.3;
}

.notification-time {
  font-size: 0.75rem;
  color: #adb5bd;
}

.notification-actions {
  flex-shrink: 0;
  margin-left: 8px;
}

.notification-footer {
  padding: 8px 16px;
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
  text-align: center;
}
</style>
